import { mapValues } from "@flaminem/utils";

import { CaseSummary } from "src/types/case";
import { ShortElement } from "src/types/element";
import { PropertyValue } from "src/types/property";

import { CasePropertyDiff, PropertyPath } from "./AlertCaseVersionsDiffModal.types";

export function getCaseVersionsPropertyDiffs_<TVersions extends Record<string, CaseSummary | null>>(
  originalVersion: CaseSummary,
  versions: TVersions
): CasePropertyDiff[] {
  const paths = getPropertyPaths(originalVersion);

  const diffs = paths.map<CasePropertyDiff<keyof TVersions>>((path) => {
    const originalElements = getElementsByPath(originalVersion, path);

    if (!originalElements) {
      throw new Error(`Property of path ${path.join(".")} not found in original case`);
    }

    const {
      property: originalProperty,
      element: originalElement,
      subElement: originalSubElement,
    } = originalElements;

    const versionsElements = mapValues(versions, (_, caseData) =>
      caseData ? getElementsByPath(caseData, path) : null
    );

    return {
      id: path.join("."),
      path,
      original: {
        property: originalProperty,
        element: originalElement,
        subElement: originalSubElement,
      },
      versions: versionsElements,
    };
  });

  return diffs;
}

/**
 * Gather paths of all properties for a given case
 * Includes both root properties and element properties
 */
const getPropertyPaths = (caseData: CaseSummary): PropertyPath[] => {
  const rootProperties = caseData.properties.map(
    (property) => [property.property.code] satisfies PropertyPath
  );

  const elementProperties = caseData.elements.flatMap((element) => [
    ...element.properties.map(
      (property) => [element.configurableType.code, property.property.code] satisfies PropertyPath
    ),
    ...element.elements.flatMap((subElement) =>
      subElement.properties.map(
        (property) =>
          [
            element.configurableType.code,
            subElement.configurableType.code,
            property.property.code,
          ] satisfies PropertyPath
      )
    ),
  ]);

  return [...rootProperties, ...elementProperties];
};

const getElementsByPath = (
  caseData: CaseSummary,
  path: PropertyPath
): {
  element: ShortElement | null;
  subElement: ShortElement | null;
  property: PropertyValue;
} | null => {
  // First check root properties
  const property = caseData.properties.find((p) => p.property.code === path[0]);

  // If found, return null because it's a property of an element
  if (property) {
    return { property, element: null, subElement: null };
  }

  // If no property found, check try to find element with current code
  const element = caseData.elements.find((e) => e.configurableType.code === path[0]);

  if (!element) {
    return null;
  }

  // If element found, check if there is a property with current code on this element
  const elementProperty = element.properties.find((p) => p.property.code === path[1]);

  if (elementProperty) {
    return {
      element,
      subElement: null,
      property: elementProperty,
    };
  }

  // If no property found, check if there is a sub element with current code on this element
  const subElement = element.elements.find((e) => e.configurableType.code === path[1]);

  if (!subElement) {
    return null;
  }

  // If sub element found, check if there is a property with current code on this sub element
  const subElementProperty = subElement.properties.find((p) => p.property.code === path[2]);

  if (subElementProperty) {
    return {
      element,
      subElement,
      property: subElementProperty,
    };
  }

  return null;
};
